// ============================================================
//  ContactSaverPage.js — Contact Saver
//
//  Anyone who messages the owner's WhatsApp and isn't saved in their phone yet
//  gets saved as "<prefix> <WhatsApp name>". The owner links WhatsApp once
//  (QR or pairing code); a background job checks every few minutes.
//  Paid monthly from the CommandKit wallet. We never read or keep chats.
// ============================================================
import './ContactSaverPage.css';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import { getContactSaver, getContactSaverRecent, contactSaverAction, exportContactSaver } from '../../api/client';
import LinkWhatsAppModal from './LinkWhatsAppModal';
import ContestPanel from './ContestPanel';

const naira = n => `₦${Number(n || 0).toLocaleString('en-NG', { maximumFractionDigits: 2 })}`;
const parse = s => (s ? new Date(String(s).replace(' ', 'T')) : null);
const day = s => (s ? parse(s).toLocaleDateString([], { day: 'numeric', month: 'short', year: 'numeric' }) : '');
const newNonce = () => `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 10)}`;
/** secs = seconds ago, worked out by the server (so the server's timezone never matters) */
function ago(secs) {
  if (secs == null) return '';
  const m = Math.floor(secs / 60);
  if (m < 1) return 'just now';
  if (m < 60) return `${m} min ago`;
  if (m < 1440) return `${Math.round(m / 60)} h ago`;
  const d = Math.round(m / 1440);
  return `${d} day${d === 1 ? '' : 's'} ago`;
}
const mmss = t => `${Math.floor(t / 60)}:${String(t % 60).padStart(2, '0')}`;
const people = n => `${n} ${n === 1 ? 'person' : 'people'}`;

/** "Is it working right now?" — a live strip that ticks every second. */
function LiveStatus({ a, loadedAt, now }) {
  const since = a.lastCheckAgo == null ? null : a.lastCheckAgo + Math.floor((now - loadedAt) / 1000);
  if (!a.running) {
    const why = !a.paid ? 'your plan isn’t active — pay below to start' : !a.on ? 'you paused it' : 'WhatsApp isn’t linked';
    return <div className="csv-live is-off"><span className="csv-dot" /><div><b>Not saving right now</b><small>Because {why}.</small></div></div>;
  }
  if (since == null) {
    return <div className="csv-live"><span className="csv-dot is-pulse" /><div><b>Live — starting up</b><small>The first check runs within 5 minutes. After that, every 5 minutes.</small></div></div>;
  }
  const late = since > 15 * 60;
  const left = Math.max(0, a.checkEvery - since);
  const pct = Math.min(100, Math.round((since / a.checkEvery) * 100));
  const found = a.lastFound ? `${people(a.lastFound)} messaged · ${a.lastSaved} new saved` : 'no new messages';
  return (
    <div className={`csv-live ${late ? 'is-late' : ''}`}>
      <span className={`csv-dot ${late ? '' : 'is-pulse'}`} />
      <div>
        <b>{late ? 'Checks are running late' : 'Live — watching for new people'}</b>
        <small>Last check {ago(since)} · {found}</small>
        {late
          ? <small>We’ll keep trying. If this lasts more than an hour, contact support.</small>
          : <>
              <span className="csv-cycle" aria-hidden="true"><i style={{ width: `${pct}%` }} /></span>
              <small>{left > 0 ? `Next check in about ${mmss(left)}` : 'Next check any moment now'}</small>
            </>}
      </div>
    </div>
  );
}
const SAMPLE = 'Tunde Bakare';

function previewName(prefix, position, name) {
  const p = (prefix || '').trim();
  if (!p) return name;
  return position === 'after' ? `${name} ${p}` : `${p} ${name}`;
}

const dotOf = n => (!n.linked ? (n.status === 'logged_out' ? 'warn' : 'idle') : !n.paid ? 'warn' : !n.on ? 'idle' : 'ok');

/** Tabs for each linked number + "Add number". */
function NumberBar({ numbers, sel, onPick, canAdd, extraPrice, onAdd, busy }) {
  const [adding, setAdding] = useState(false);
  const [label, setLabel] = useState('');
  const submit = async () => { if (await onAdd(label.trim())) { setAdding(false); setLabel(''); } };
  return (
    <div className="csv-numbers" role="tablist" aria-label="Your WhatsApp numbers">
      {numbers.map(n => (
        <button key={n.id} type="button" role="tab" aria-selected={n.id === sel} className={`csv-num ${n.id === sel ? 'is-on' : ''}`} onClick={() => onPick(n.id)}>
          <i className={`csv-num-dot is-${dotOf(n)}`} aria-hidden="true" />
          <span><b>{n.label}</b><small>{n.phone || 'Not linked'}{n.linked && n.stats ? ` · ${n.stats.today} today` : ''}</small></span>
        </button>
      ))}
      {canAdd && !adding && (
        <button type="button" className="csv-num csv-num-add" onClick={() => setAdding(true)}>
          <i className="fa-solid fa-plus" aria-hidden="true" />
          <span><b>Add number</b><small>{naira(extraPrice)}/month</small></span>
        </button>
      )}
      {adding && (
        <form className="csv-num-form" onSubmit={e => { e.preventDefault(); submit(); }}>
          <label className="csv-sr" htmlFor="csv-new-label">Name for the new number</label>
          <input id="csv-new-label" autoFocus value={label} maxLength={40} onChange={e => setLabel(e.target.value)} placeholder="e.g. Sales, Support" />
          <button type="submit" className="csv-btn csv-btn-dark csv-btn-sm" disabled={busy}>Add</button>
          <button type="button" className="csv-btn csv-btn-ghost csv-btn-sm" onClick={() => setAdding(false)}>Cancel</button>
        </form>
      )}
    </div>
  );
}

export default function ContactSaverPage() {
  const [view, setView] = useState(() => (window.location.hash === '#contest' ? 'contest' : 'saver'));
  const pickView = v => { setView(v); try { window.history.replaceState(null, '', v === 'contest' ? '#contest' : window.location.pathname); } catch { /* ignore */ } };
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [linking, setLinking] = useState(false);
  const [busy, setBusy] = useState('');
  const [confirmPay, setConfirmPay] = useState(null);
  const [short, setShort] = useState('');
  const [confirmUnlink, setConfirmUnlink] = useState(false);
  const [form, setForm] = useState({ prefix: 'CK', position: 'before', fallback: 'Customer' });
  const [more, setMore] = useState(true);
  const [loadedAt, setLoadedAt] = useState(Date.now());
  const touched = useRef(false);   // don't overwrite the name settings while the user is typing
  const [now, setNow] = useState(Date.now());
  const [sel, setSel] = useState(0);              // selected number's id (0 = first)
  const [renaming, setRenaming] = useState(null);  // label being typed
  const [confirmRemove, setConfirmRemove] = useState(false);
  const selRef = useRef(0);

  const load = useCallback(async (acc = selRef.current, fresh = false) => {
    try {
      const res = await getContactSaver(acc);
      if (acc !== selRef.current && !fresh) return;   // the user switched numbers meanwhile
      if (fresh) touched.current = false;
      selRef.current = res.data.account.id; setSel(res.data.account.id);
      setData(res.data);
      const a = res.data.account;
      if (!touched.current) setForm({ prefix: a.prefix, position: a.position, fallback: a.fallback });
      setMore((res.data.recent || []).length >= 30);
      setLoadedAt(Date.now());
      setError('');
    } catch (e) { setError(e?.response?.data?.message || 'Could not load Contact Saver'); }
  }, []);
  useEffect(() => { load(0, true); }, [load]);
  // tick every second for the countdown; refresh from the server every minute while the tab is open
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    const r = setInterval(() => { if (!document.hidden) load(); }, 60000);
    return () => { clearInterval(t); clearInterval(r); };
  }, [load]);

  const edit = fn => { touched.current = true; setForm(fn); };
  const a = data?.account;
  const setAccount = acc => setData(d => ({ ...d, account: acc, price: acc.price ?? d.price,
    numbers: d.numbers.map(n => (n.id === acc.id ? { ...n, ...acc } : n)) }));
  const call = (action, extra = {}) => contactSaverAction(action, { acc: a.id, ...extra });
  function pick(id) {
    if (id === sel) return;
    selRef.current = id; setSel(id);
    setConfirmPay(null); setShort(''); setConfirmUnlink(false); setConfirmRemove(false); setRenaming(null);
    load(id, true);
  }
  async function addNumber(label) {
    setBusy('add');
    try {
      const res = await contactSaverAction('add_number', { label });
      toast.success(res.data.message);
      await load(res.data.account.id, true);
      selRef.current = res.data.account.id;
      return true;
    } catch (e) { toast.error(e?.response?.data?.message || 'Could not add'); return false; }
    finally { setBusy(''); }
  }
  async function rename() {
    const label = (renaming || '').trim();
    if (!label || label === a.label) { setRenaming(null); return; }
    const res = await act('rename', () => call('rename', { label }));
    if (res) setRenaming(null);
  }
  async function removeNumber() {
    if (!confirmRemove) { setConfirmRemove(true); return; }
    setConfirmRemove(false); setBusy('remove');
    try {
      const res = await call('remove');
      toast.success(res.data.message);
      await load(res.data.first.id, true);
    } catch (e) { toast.error(e?.response?.data?.message || 'Could not remove'); }
    setBusy('');
  }
  const dirty = a && (form.prefix.trim() !== a.prefix || form.position !== a.position || form.fallback.trim() !== a.fallback);
  const sample = useMemo(() => previewName(form.prefix, form.position, SAMPLE), [form.prefix, form.position]);
  const noName = useMemo(() => previewName(form.prefix, form.position, `${(form.fallback || 'Customer').trim()} 0198`), [form]);

  async function act(key, fn, okMsg) {
    setBusy(key);
    try { const res = await fn(); if (res.data.account) setAccount(res.data.account); toast.success(okMsg || res.data.message); return res; }
    catch (e) { toast.error(e?.response?.data?.message || 'Something went wrong'); return null; }
    finally { setBusy(''); }
  }

  const saveSettings = () => { touched.current = false; return act('settings', () => call('settings', { prefix: form.prefix.trim(), position: form.position, fallback: form.fallback.trim() }), 'Saved — new contacts will use this name'); };
  const toggleOn = () => act('on', () => call('settings', { on: !a.on }), a.on ? 'Paused — nothing will be saved' : 'Saving new contacts again');
  const setRenew = on => act('renew', () => call('autorenew', { on }));

  async function pay() {
    if (!confirmPay) { setConfirmPay(newNonce()); setShort(''); return; }
    setBusy('pay');
    try {
      const res = await call('pay', { nonce: confirmPay });
      setAccount(res.data.account);
      toast.success(res.data.message);
    } catch (e) {
      const d = e?.response?.data || {};
      if (d.code === 'insufficient') setShort(d.message); else toast.error(d.message || 'Payment failed — nothing was charged');
    }
    setConfirmPay(null); setBusy('');
  }

  async function unlink() {
    if (!confirmUnlink) { setConfirmUnlink(true); return; }
    setConfirmUnlink(false);
    await act('unlink', () => call('unlink'));
  }

  async function exportVcf() {
    setBusy('export');
    try {
      const res = await exportContactSaver(a.id);
      const url = URL.createObjectURL(new Blob([res.data], { type: 'text/vcard' }));
      const el = document.createElement('a');
      el.href = url; el.download = `commandkit-contacts-${a.label.toLowerCase().replace(/[^a-z0-9]+/g, '-')}.vcf`; el.click();
      setTimeout(() => URL.revokeObjectURL(url), 2000);
    } catch { toast.error('Could not export'); }
    setBusy('');
  }

  async function loadMore() {
    const last = data.recent[data.recent.length - 1];
    if (!last) return;
    setBusy('more');
    try {
      const res = await getContactSaverRecent(a.id, last.id);
      setData(d => ({ ...d, recent: [...d.recent, ...res.data.recent] }));
      setMore(res.data.recent.length >= 30);
    } catch { toast.error('Could not load more'); }
    setBusy('');
  }

  if (error && !data) return <div className="csv"><div className="csv-empty"><p>{error}</p><button type="button" className="csv-btn csv-btn-dark" onClick={() => load(selRef.current, true)}>Try again</button></div></div>;
  if (!data) return <div className="csv"><div className="csv-loading"><span className="csv-spin" /> Loading…</div></div>;

  const linked = a.linked;
  const status = !linked ? (a.status === 'logged_out' ? ['warn', 'Unlinked'] : ['idle', 'Not linked'])
    : !a.paid ? ['warn', 'Not active'] : !a.on ? ['idle', 'Paused'] : ['ok', 'Running'];

  return (
    <div className="csv">
      <header className="csv-head">
        <div>
          <h1>Contact Saver</h1>
          <p>Anyone who messages your WhatsApp and isn’t saved yet is added to your phone’s contacts — with your prefix on their name. We never read or keep your chats.</p>
        </div>
        <span className={`csv-chip is-${status[0]}`}><i />{a.label} · {status[1]}</span>
      </header>

      <div className="rc-views" role="tablist" aria-label="Contact Saver">
        <button type="button" role="tab" aria-selected={view === 'saver'} className={view === 'saver' ? 'is-on' : ''} onClick={() => pickView('saver')}><i className="fa-solid fa-address-book" /> Saving contacts</button>
        <button type="button" role="tab" aria-selected={view === 'contest'} className={view === 'contest' ? 'is-on' : ''} onClick={() => pickView('contest')}><i className="fa-solid fa-trophy" /> Referral contest</button>
      </div>

      {view === 'contest' ? <ContestPanel /> : (<>
      {(data.numbers.length > 1 || data.numbers.length < data.maxNumbers) && (
        <NumberBar numbers={data.numbers} sel={sel} onPick={pick} canAdd={data.numbers.length < data.maxNumbers}
          extraPrice={data.prices?.extra} onAdd={addNumber} busy={busy === 'add'} />
      )}

      {/* ── link ── */}
      {!linked ? (
        <section className="csv-card csv-hero">
          <div className="csv-hero-ic"><i className="fa-brands fa-whatsapp" /></div>
          <div className="csv-hero-body">
            <h2>{a.status === 'logged_out' ? `Link “${a.label}” again` : data.numbers.length > 1 ? `Link WhatsApp to “${a.label}”` : 'Link your WhatsApp'}</h2>
            <p>{a.status === 'logged_out'
              ? 'WhatsApp removed CommandKit from your linked devices (or your phone was offline for a long time). Link again to keep saving new contacts.'
              : 'Scan a QR code from WhatsApp → Linked devices, just like WhatsApp Web. Takes about a minute.'}</p>
            <ul className="csv-ticks">
              <li><i className="fa-solid fa-check" />Your chats stay private — we only see who messaged</li>
              <li><i className="fa-solid fa-check" />Groups and channels are ignored</li>
              <li><i className="fa-solid fa-check" />People already in your contacts are never touched</li>
            </ul>
          </div>
          <button type="button" className="csv-btn csv-btn-brand" onClick={() => setLinking(true)}><i className="fa-solid fa-qrcode" /> Link WhatsApp</button>
        </section>
      ) : (
        <section className="csv-card csv-linked" aria-label="Linked WhatsApp">
          <span className="csv-phone-ic"><i className="fa-solid fa-mobile-screen" /></span>
          <div className="csv-linked-body">
            {renaming === null ? (
              <span className="csv-label">{a.label}
                <button type="button" className="csv-icon-btn" aria-label={`Rename ${a.label}`} onClick={() => setRenaming(a.label)}><i className="fa-solid fa-pen" /></button>
              </span>
            ) : (
              <form className="csv-rename" onSubmit={e => { e.preventDefault(); rename(); }}>
                <label className="csv-sr" htmlFor="csv-rename">Name for this number</label>
                <input id="csv-rename" autoFocus value={renaming} maxLength={40} onChange={e => setRenaming(e.target.value)} onKeyDown={e => { if (e.key === 'Escape') setRenaming(null); }} />
                <button type="submit" className="csv-btn csv-btn-dark csv-btn-sm" disabled={busy === 'rename'}>Save</button>
              </form>
            )}
            <b>{a.phone}{a.waName ? ` · ${a.waName}` : ''}</b>
            <span>Linked {day(a.linkedAt)}</span>
            {a.lastError && <span className="csv-err-line"><i className="fa-solid fa-triangle-exclamation" /> {a.lastError}</span>}
          </div>
          <div className="csv-linked-actions">
            <button type="button" className="csv-btn csv-btn-ghost csv-btn-sm" onClick={toggleOn} disabled={busy === 'on'}>
              <i className={`fa-solid ${a.on ? 'fa-pause' : 'fa-play'}`} /> {a.on ? 'Pause' : 'Resume'}
            </button>
            <button type="button" className={`csv-btn csv-btn-sm ${confirmUnlink ? 'csv-btn-dangerfill' : 'csv-btn-danger'}`} onClick={unlink}
              onBlur={() => setConfirmUnlink(false)} disabled={busy === 'unlink'}>
              {busy === 'unlink' ? <span className="csv-spin" /> : <i className="fa-solid fa-link-slash" />} {confirmUnlink ? 'Tap again to unlink' : 'Unlink'}
            </button>
          </div>
        </section>
      )}

      {linked && <LiveStatus a={a} loadedAt={loadedAt} now={now} />}

      {/* ── plan ── */}
      <section className={`csv-card csv-plan ${a.paid ? 'is-on' : ''}`}>
        <div className="csv-plan-top">
          <div>
            <h2>{a.paid ? 'Active' : 'Turn it on'}</h2>
            <p>{a.paid ? `Paid until ${day(a.paidUntil)}` : 'New contacts are saved while your plan is active.'}</p>
          </div>
          <div className="csv-price">{naira(data.price)}<small>/month</small>{data.prices && data.price < data.prices.first && <em>extra number</em>}</div>
        </div>
        {a.paid && (
          <label className="csv-row">
            <span><b>Auto-renew from wallet</b><small>We renew a day before it ends</small></span>
            <button type="button" role="switch" aria-checked={a.autoRenew} aria-label="Auto-renew" className={`csv-switch ${a.autoRenew ? 'is-on' : ''}`}
              onClick={() => setRenew(!a.autoRenew)} disabled={busy === 'renew'}><span /></button>
          </label>
        )}
        <button type="button" className={`csv-btn ${a.paid ? 'csv-btn-ghost csv-btn-sm csv-self-start' : 'csv-btn-dark'} ${confirmPay ? 'is-ask' : ''}`}
          onClick={pay} onBlur={() => setConfirmPay(null)} disabled={busy === 'pay'}>
          {busy === 'pay' ? <span className="csv-spin" /> : <i className="fa-solid fa-wallet" />}
          {confirmPay ? `Tap again to pay ${naira(data.price)} from wallet` : a.paid ? 'Add another month' : `Pay ${naira(data.price)} from wallet`}
        </button>
        {short && <div className="csv-short"><span>{short}</span><Link className="csv-btn csv-btn-dark csv-btn-sm" to="/wallet">Top up wallet</Link></div>}
      </section>

      {/* ── stats ── */}
      <div className="csv-stats">
        <div className="csv-stat"><span>Saved today</span><b>{data.stats.today}</b></div>
        <div className="csv-stat"><span>Saved this month</span><b>{data.stats.month}</b></div>
        <div className="csv-stat"><span>Saved in total</span><b>{data.stats.total}</b></div>
        <div className="csv-stat"><span>Skipped</span><b>{data.stats.skipped}</b><small>number hidden or save failed</small></div>
      </div>

      {/* ── names ── */}
      <section className="csv-card" aria-labelledby="csv-names">
        <h2 id="csv-names">How names are saved</h2>
        <div className="csv-grid">
          <label className="csv-field">Prefix
            <input value={form.prefix} maxLength={24} onChange={e => edit(f => ({ ...f, prefix: e.target.value }))} placeholder="e.g. CK" />
          </label>
          <div className="csv-field">
            <span>Put it</span>
            <div className="csv-seg" role="group" aria-label="Prefix position">
              {[['before', 'Before the name'], ['after', 'After the name']].map(([k, l]) => (
                <button key={k} type="button" aria-pressed={form.position === k} className={form.position === k ? 'is-on' : ''}
                  onClick={() => edit(f => ({ ...f, position: k }))}>{l}</button>
              ))}
            </div>
          </div>
          <label className="csv-field">If they have no WhatsApp name, call them
            <input value={form.fallback} maxLength={30} onChange={e => edit(f => ({ ...f, fallback: e.target.value }))} placeholder="Customer" />
            <small>followed by the last 4 digits of their number</small>
          </label>
        </div>
        <div className="csv-preview">
          <span className="csv-preview-k">Preview</span>
          <div className="csv-preview-row">
            <span className="csv-av">{sample.charAt(0).toUpperCase()}</span>
            <div><b>{sample}</b><small>someone called “{SAMPLE}” on WhatsApp</small></div>
          </div>
          <div className="csv-preview-row">
            <span className="csv-av is-alt">{noName.charAt(0).toUpperCase()}</span>
            <div><b>{noName}</b><small>someone with no WhatsApp name</small></div>
          </div>
        </div>
        <div className="csv-tip"><i className="fa-solid fa-circle-info" />
          <span>Contacts are saved to your phone’s <b>default account</b>. To have them backed up to Google, open your phone’s Contacts settings and make your Google account the default.</span>
        </div>
        <div className="csv-actions">
          <button type="button" className="csv-btn csv-btn-dark" onClick={saveSettings} disabled={!dirty || busy === 'settings'}>
            {busy === 'settings' ? <span className="csv-spin" /> : null} Save
          </button>
        </div>
      </section>

      {/* ── recent ── */}
      <section className="csv-card" aria-labelledby="csv-recent">
        <div className="csv-card-head">
          <h2 id="csv-recent">Recently saved</h2>
          <button type="button" className="csv-btn csv-btn-ghost csv-btn-sm" onClick={exportVcf} disabled={busy === 'export' || !data.stats.total}>
            <i className="fa-solid fa-file-arrow-down" /> Export .vcf
          </button>
        </div>
        {data.recent.length === 0 ? (
          <p className="csv-muted">{linked && a.paid ? 'Nobody new yet. When someone who isn’t in your contacts messages you, they’ll appear here within a few minutes.' : 'Saved contacts will appear here.'}</p>
        ) : (
          <div className="csv-table-wrap">
            <table className="csv-table">
              <thead><tr><th>Saved as</th><th>Number</th><th>When</th><th>Status</th></tr></thead>
              <tbody>
                {data.recent.map(r => (
                  <tr key={r.id}>
                    <td><b>{r.name}</b></td>
                    <td className="csv-mono">{r.phone}</td>
                    <td>{ago(r.ago == null ? null : r.ago + Math.floor((now - loadedAt) / 1000))}</td>
                    <td><span className={`csv-pill ${r.ok ? 'is-ok' : 'is-bad'}`} title={r.error || ''}>{r.ok ? 'On phone' : 'Failed — will retry'}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {more && data.recent.length > 0 && (
          <button type="button" className="csv-btn csv-btn-ghost csv-btn-sm csv-self-center" onClick={loadMore} disabled={busy === 'more'}>Show more</button>
        )}
      </section>

      {(data.numbers.length > 1 || linked) && (
        <div className="csv-danger">
          <span><b>Remove “{a.label}”</b><small>Unlinks it and deletes its saved list here (contacts already on the phone stay).{a.paid ? ' Unused paid days are not refunded.' : ''}</small></span>
          <button type="button" className={`csv-btn csv-btn-sm ${confirmRemove ? 'csv-btn-dangerfill' : 'csv-btn-danger'}`} onClick={removeNumber}
            onBlur={() => setConfirmRemove(false)} disabled={busy === 'remove'}>
            {busy === 'remove' ? <span className="csv-spin" /> : <i className="fa-solid fa-trash" />} {confirmRemove ? 'Tap again to remove' : 'Remove number'}
          </button>
        </div>
      )}

      </>)}

      {linking && <LinkWhatsAppModal acc={a.id} label={a.label} onClose={() => setLinking(false)} onLinked={() => { setLinking(false); load(a.id, true); }} />}
    </div>
  );
}
